"use client";

import React, { useState } from "react";
import { Eye, Loader, ChevronLeft, ChevronRight, PackageX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
  ProductSortDropdown,
  ProductsFilter,
  type EcommerceProduct
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";

function ProductsGridConsumer() {
  const { 
    products, 
    loading, 
    error, 
    refetch, 
    hasNext, 
    hasPrev, 
    nextPage, 
    prevPage, 
    currentPage, 
    totalPages, 
    isRefetching 
  } = useEcommerceProducts();
  
  const [selectedProduct, setSelectedProduct] = useState<EcommerceProduct | null>(null);

  if (loading && !isRefetching && products.length === 0) {
    return (
      <div data-section-id="222414" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} data-index={i} className="flex flex-col gap-4">
            <div className="aspect-[4/5] rounded-sm bg-muted/50 animate-pulse" />
            <div className="space-y-2">
              <div className="h-4 w-2/3 bg-muted/50 rounded-sm animate-pulse" />
              <div className="h-4 w-1/3 bg-muted/50 rounded-sm animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center space-y-4 bg-muted/20 rounded-sm border border-border border-dashed">
        <PackageX className="w-12 h-12 text-muted-foreground" />
        <div className="space-y-1">
          <h3 className="text-lg font-default font-semibold text-foreground">Greška pri učitavanju proizvoda</h3>
          <p className="text-sm font-sans text-muted-foreground">{error}</p>
        </div>
        <Button onClick={refetch} variant="outline" className="font-default rounded-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground">Pokušajte ponovno</Button>
      </div>
    );
  }

  if (products.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center space-y-4 bg-muted/20 rounded-sm border border-border border-dashed">
        <PackageX className="w-12 h-12 text-muted-foreground" />
        <div className="space-y-1">
          <h3 className="text-lg font-default font-semibold text-foreground">Nema pronađenih proizvoda</h3>
          <p className="text-sm font-sans text-muted-foreground">Pokušajte prilagoditi filtre ili pojam za pretragu.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {products.map((product, i) => (
          <div key={product.id} data-index={i} className="group flex flex-col bg-card rounded-sm border border-border shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
            <div className="relative aspect-[4/5] bg-muted/30 overflow-hidden">
              {product.featured_image_url ? (
                <img 
                  src={product.featured_image_url} 
                  alt={product.title} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-muted/50 font-sans">
                  Nema slike
                </div>
              )}
              
              {}
              <div className="absolute inset-x-0 bottom-0 p-4 opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 bg-gradient-to-t from-primary/80 to-transparent flex justify-center">
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="w-full bg-background/95 hover:bg-primary hover:text-primary-foreground text-foreground backdrop-blur-sm font-default rounded-sm transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedProduct(product);
                  }}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Brzi pregled
                </Button>
              </div>
            </div>

            <div className="p-5 flex flex-col flex-grow space-y-3">
              <div className="space-y-1 flex-grow">
                <Link to={product.link} className="hover:underline decoration-primary-foreground underline-offset-4">
                  <h3 className="font-default font-semibold text-foreground line-clamp-2 leading-tight tracking-tight">
                    {product.title}
                  </h3>
                </Link>
                {product.price !== undefined && (
                  <div className="text-xl font-default font-bold text-primary">
                    {product.price.toFixed(2)} €
                  </div>
                )}
              </div>
              
              <div className="pt-2 mt-auto">
                <AddToCartButton 
                  productId={product.id} 
                  className="w-full" 
                  buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-default rounded-sm transition-all shadow-sm hover:shadow-md" 
                  text="Dodaj u košaricu" 
                  showQuantity={false} 
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-4 pt-8 border-t border-border">
          <Button 
            variant="outline" 
            onClick={prevPage} 
            disabled={!hasPrev || isRefetching}
            className="w-28 font-default rounded-sm border-border text-primary hover:bg-secondary"
          >
            {isRefetching && !hasPrev ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : <ChevronLeft className="w-4 h-4 mr-2" />}
            Prethodno
          </Button>
          <span className="text-sm font-default font-medium text-muted-foreground min-w-[100px] text-center">
            Stranica {currentPage} od {totalPages}
          </span>
          <Button 
            variant="outline" 
            onClick={nextPage} 
            disabled={!hasNext || isRefetching}
            className="w-28 font-default rounded-sm border-border text-primary hover:bg-secondary"
          >
            Sljedeće
            {isRefetching && hasNext ? <Loader className="w-4 h-4 ml-2 animate-spin" /> : <ChevronRight className="w-4 h-4 ml-2" />}
          </Button>
        </div>
      )}

      {}
      <Dialog open={!!selectedProduct} onOpenChange={(open) => !open && setSelectedProduct(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-card border-border gap-0 rounded-sm" showCloseButton={true}>
          <DialogTitle className="sr-only">Brzi pregled: {selectedProduct?.title}</DialogTitle>
          <DialogDescription className="sr-only">Detalji proizvoda i opcije kupnje</DialogDescription>
          
          {selectedProduct && (
            <div className="grid grid-cols-1 md:grid-cols-2 h-full max-h-[85vh] md:max-h-[600px]">
              <div className="relative bg-muted/30 h-64 md:h-full">
                {selectedProduct.featured_image_url ? (
                  <img 
                    src={selectedProduct.featured_image_url} 
                    alt={selectedProduct.title} 
                    className="absolute inset-0 w-full h-full object-cover" 
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-muted-foreground font-sans">
                    Slika nije dostupna
                  </div>
                )}
              </div>
              
              <div className="p-6 md:p-8 flex flex-col h-full overflow-y-auto">
                <DialogHeader className="text-left space-y-4 mb-6">
                  <h2 className="text-2xl md:text-3xl font-default font-bold text-foreground leading-tight tracking-tight">
                    {selectedProduct.title}
                  </h2>
                  <div className="text-3xl font-default font-bold text-primary">
                    {selectedProduct.price?.toFixed(2)} €
                  </div>
                </DialogHeader>

                {selectedProduct.short_description && (
                  <div 
                    className="text-muted-foreground text-sm space-y-4 mb-8 font-sans prose prose-sm dark:prose-invert max-w-none" 
                    dangerouslySetInnerHTML={{ __html: selectedProduct.short_description }} 
                  />
                )}

                <div className="mt-auto pt-6 border-t border-border space-y-4">
                  <AddToCartButton 
                    productId={selectedProduct.id} 
                    className="w-full" 
                    buttonClassName="w-full bg-primary text-primary-foreground py-6 text-lg font-default font-bold hover:bg-primary/90 transition-colors rounded-sm shadow-md" 
                    text="Dodaj u košaricu" 
                    showQuantity={true} 
                  />
                  <div className="text-center">
                    <Link 
                      to={selectedProduct.link} 
                      className="text-sm font-default text-muted-foreground hover:text-primary underline underline-offset-4 transition-colors"
                      onClick={() => setSelectedProduct(null)}
                    >
                      Pogledaj sve detalje
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ShopContent() {
  
  const current_wp_query = typeof window !== 'undefined' ? (window as any).wvcClient?.getCurrentWPQuery() : undefined;

  return (
    <section id="shop-content" className="bg-background py-16 md:py-24 text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <EcommerceProductsProvider wp_query={current_wp_query}>
          <div className="flex flex-col lg:flex-row gap-8 xl:gap-12">
            
            {}
            <aside className="w-full lg:w-1/4 shrink-0 space-y-6">
              <div className="sticky top-24 bg-card border border-border rounded-sm p-5 shadow-sm">
                <h2 className="text-lg font-default font-bold text-primary mb-4 pb-4 border-b border-border tracking-tight">
                  Filtri
                </h2>
                <ProductsFilter />
              </div>
            </aside>

            {}
            <div className="w-full lg:w-3/4 flex flex-col space-y-6">
              {}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-card border border-border rounded-sm p-4 shadow-sm">
                <h1 className="text-xl font-default font-bold text-primary tracking-tight">
                  Originalni i premium dijelovi
                </h1>
                <div className="w-full sm:w-auto">
                  <ProductSortDropdown label="Poredaj po" />
                </div>
              </div>

              {}
              <ProductsGridConsumer />
            </div>

          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}